<template>
  <div class="revisionhub-home">

    <!-- Hero Section -->
    <section class="hero-section text-white">
      <div class="container">
        <div class="row align-items-center min-vh-75">
          <div class="col-lg-7 text-center text-lg-start">
            <span class="badge rounded-pill bg-light text-primary px-3 py-2 mb-3">
              Smart Revision & Question Bank Platform
            </span>

            <h1 class="display-4 fw-bold mb-3">
              Welcome to RevisionHub
            </h1>

            <p class="lead mb-4">
              A modern learning platform that helps students revise smarter,
              practice questions, and prepare confidently for exams.
            </p>

            <div class="d-flex gap-3 justify-content-center justify-content-lg-start flex-wrap">
              <button @click="goToApp" class="btn btn-light btn-lg px-4">
                Get Started
              </button>

              <a href="#features" class="btn btn-outline-light btn-lg px-4">
                Explore Features
              </a>
            </div>
          </div>

          <div class="col-lg-5 mt-5 mt-lg-0">
            <div class="hero-card shadow-lg">
              <i class="bi bi-mortarboard-fill hero-icon"></i>
              <h4 class="fw-bold mt-3">Revise. Practice. Improve.</h4>
              <p class="mb-0">
                Access organized questions, track progress, and strengthen weak areas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="py-5 bg-light">
      <div class="container">
        <div class="text-center mb-5">
          <h2 class="fw-bold">What RevisionHub Offers</h2>
          <p class="text-muted">
            Everything needed for effective revision and exam preparation.
          </p>
        </div>

        <div class="row g-4">
          <div class="col-md-4">
            <div class="feature-card h-100">
              <i class="bi bi-journal-check feature-icon"></i>
              <h5>Question Bank</h5>
              <p>
                Organize questions by subject, topic, class, and difficulty level
                for easy access and revision.
              </p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="feature-card h-100">
              <i class="bi bi-pencil-square feature-icon"></i>
              <h5>Practice & Assessment</h5>
              <p>
                Students can practice questions, take quizzes, and receive instant
                feedback to improve performance.
              </p>
            </div>
          </div>

          <div class="col-md-4">
            <div class="feature-card h-100">
              <i class="bi bi-graph-up-arrow feature-icon"></i>
              <h5>Progress Tracking</h5>
              <p>
                Monitor scores, identify weak areas, and support learners with
                data-driven revision insights.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Screenshots Section -->
    <section class="py-5">
      <div class="container text-center">
        <h2 class="fw-bold mb-3">RevisionHub in Action</h2>
        <p class="text-muted mb-5">
          A simple and friendly interface for both teachers and students.
        </p>

        <div id="screenshotCarousel" class="carousel slide carousel-dark" data-bs-ride="carousel">
          <div class="carousel-inner">
            <div
              class="carousel-item"
              :class="{ active: idx === 0 }"
              v-for="(shot, idx) in screenshots"
              :key="idx"
            >
              <img
                :src="shot"
                class="screenshot-img"
                alt="RevisionHub screenshot"
              >
            </div>
          </div>

          <button class="carousel-control-prev" type="button" data-bs-target="#screenshotCarousel" data-bs-slide="prev">
            <span class="carousel-control-prev-icon"></span>
            <span class="visually-hidden">Previous</span>
          </button>

          <button class="carousel-control-next" type="button" data-bs-target="#screenshotCarousel" data-bs-slide="next">
            <span class="carousel-control-next-icon"></span>
            <span class="visually-hidden">Next</span>
          </button>

          <div class="carousel-indicators">
            <button
              v-for="(shot, i) in screenshots"
              :key="i"
              type="button"
              data-bs-target="#screenshotCarousel"
              :data-bs-slide-to="i"
              :class="{ active: i === 0 }"
              :aria-label="'Slide ' + (i + 1)"
            ></button>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section class="py-5 bg-light">
      <div class="container">
        <div class="about-box text-center mx-auto">
          <h2 class="fw-bold mb-3">Why RevisionHub?</h2>
          <p class="lead text-muted mb-0">
            RevisionHub helps schools, teachers, and students make revision more
            structured, interactive, and result-oriented. It connects question
            management, student practice, and performance tracking in one place.
          </p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section text-white text-center py-5">
      <div class="container">
        <h2 class="fw-bold mb-3">Ready to Start Revising Smarter?</h2>
        <p class="mb-4">
          Join RevisionHub and make exam preparation easier and more effective.
        </p>
        <button @click="goToApp" class="btn btn-light btn-lg px-5">
          Start Now
        </button>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const goToApp = () => {
  router.push('/login');
};

const screenshots = ref([
  '/screenshots/dashboard.png',
  '/screenshots/question-list.png',
  '/screenshots/assessment-creation.png',
]);

onMounted(() => {
  const carouselEl = document.querySelector('#screenshotCarousel');

  if (carouselEl && window.bootstrap) {
    new window.bootstrap.Carousel(carouselEl, {
      interval: 7000,
      ride: 'carousel',
      pause: 'hover',
      wrap: true,
    });
  }
});
</script>

<style scoped>
.revisionhub-home {
  font-family: inherit;
}

.hero-section {
  background: linear-gradient(135deg, #0d6efd 0%, #083b7a 100%);
  padding: 80px 0;
}

.min-vh-75 {
  min-height: 75vh;
}

.hero-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  backdrop-filter: blur(12px);
}

.hero-icon {
  font-size: 4rem;
  color: #ffffff;
}

.feature-card {
  background: #ffffff;
  border-radius: 18px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(13, 110, 253, 0.08);
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 18px 40px rgba(13, 110, 253, 0.15);
}

.feature-icon {
  font-size: 3rem;
  color: #0d6efd;
  margin-bottom: 18px;
}

.feature-card h5 {
  font-weight: 700;
  margin-bottom: 12px;
}

.feature-card p {
  color: #6c757d;
  font-size: 0.95rem;
  margin-bottom: 0;
}

.screenshot-img {
  width: 100%;
  max-width: 900px;
  max-height: 520px;
  object-fit: contain;
  border-radius: 18px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
}

.carousel-indicators {
  position: static;
  margin-top: 24px;
}

.about-box {
  max-width: 850px;
}

.cta-section {
  background: linear-gradient(135deg, #083b7a 0%, #0d6efd 100%);
}

@media (max-width: 768px) {
  .hero-section {
    padding: 60px 0;
  }

  .display-4 {
    font-size: 2.4rem;
  }

  .hero-card {
    padding: 30px 20px;
  }
}
</style>